import { useState } from 'react';
import { Shield, Lock, Eye, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar.tsx';

type Language = 'en' | 'fr' | 'ar';

const translations = {
  en: {
    title: 'Security',
    lastUpdated: 'Last updated: February 2026',
    backToHome: 'Back to Home',
    subtitle: 'Your data security is our top priority',
    sections: [
      {
        icon: Shield,
        title: 'Enterprise-Grade Encryption',
        content: 'All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption. Our security protocols meet international standards including ISO 27001 and SOC 2 Type II.'
      },
      {
        icon: Lock,
        title: 'Access Controls',
        content: 'Multi-factor authentication (MFA) is required for all accounts. We implement role-based access controls and regular security audits to ensure only authorized personnel can access your data.'
      },
      {
        icon: Eye,
        title: 'Monitoring & Detection',
        content: 'Our systems are monitored 24/7 with automated threat detection and response. We maintain comprehensive audit logs and conduct regular penetration testing by certified security professionals.'
      },
      {
        icon: CheckCircle,
        title: 'Compliance & Certifications',
        content: 'We comply with GDPR, CCPA, and local data protection regulations. Our infrastructure is hosted in secure, certified data centers with 99.9% uptime guarantee.'
      }
    ],
    commitment: {
      title: 'Our Security Commitment',
      points: [
        'Regular security updates and patches',
        'Employee security training and background checks',
        'Incident response plan with 24-hour notification',
        'Data backup and disaster recovery procedures',
        'Third-party security audits and assessments'
      ]
    },
    contact: {
      title: 'Security Concerns?',
      content: 'If you have any security concerns or want to report a vulnerability, please contact our security team immediately at security@karia.tn'
    }
  },
  fr: {
    title: 'Sécurité',
    lastUpdated: 'Dernière mise à jour : Février 2026',
    backToHome: 'Retour à l\'accueil',
    subtitle: 'La sécurité de vos données est notre priorité absolue',
    sections: [
      {
        icon: Shield,
        title: 'Chiffrement de Niveau Entreprise',
        content: 'Toutes les données sont chiffrées en transit avec TLS 1.3 et au repos avec le chiffrement AES-256. Nos protocoles de sécurité répondent aux normes internationales notamment ISO 27001 et SOC 2 Type II.'
      },
      {
        icon: Lock,
        title: 'Contrôles d\'Accès',
        content: 'L\'authentification multi-facteurs (MFA) est obligatoire pour tous les comptes. Nous implémentons des contrôles d\'accès basés sur les rôles et des audits de sécurité réguliers.'
      },
      {
        icon: Eye,
        title: 'Surveillance et Détection',
        content: 'Nos systèmes sont surveillés 24h/24 avec détection et réponse automatisées aux menaces. Nous maintenons des journaux d\'audit complets et effectuons des tests de pénétration réguliers.'
      },
      {
        icon: CheckCircle,
        title: 'Conformité et Certifications',
        content: 'Nous nous conformons au RGPD, CCPA et aux réglementations locales de protection des données. Notre infrastructure est hébergée dans des centres de données sécurisés et certifiés.'
      }
    ],
    commitment: {
      title: 'Notre Engagement Sécurité',
      points: [
        'Mises à jour et correctifs de sécurité réguliers',
        'Formation sécurité et vérifications d\'antécédents des employés',
        'Plan de réponse aux incidents avec notification 24h',
        'Procédures de sauvegarde et de reprise après sinistre',
        'Audits et évaluations de sécurité par des tiers'
      ]
    },
    contact: {
      title: 'Préoccupations Sécurité?',
      content: 'Si vous avez des préoccupations de sécurité ou souhaitez signaler une vulnérabilité, veuillez contacter notre équipe sécurité immédiatement à security@karia.tn'
    }
  },
  ar: {
    title: 'الأمان',
    lastUpdated: 'آخر تحديث: فبراير 2026',
    backToHome: 'العودة للرئيسية',
    subtitle: 'أمان بياناتك هو أولويتنا القصوى',
    sections: [
      {
        icon: Shield,
        title: 'تشفير بمستوى المؤسسات',
        content: 'جميع البيانات مشفرة أثناء النقل باستخدام TLS 1.3 وأثناء التخزين باستخدام تشفير AES-256. بروتوكولات الأمان لدينا تلبي المعايير الدولية بما في ذلك ISO 27001 و SOC 2 Type II.'
      },
      {
        icon: Lock,
        title: 'ضوابط الوصول',
        content: 'المصادقة متعددة العوامل (MFA) مطلوبة لجميع الحسابات. ننفذ ضوابط وصول قائمة على الأدوار وعمليات تدقيق أمنية منتظمة لضمان وصول الأشخاص المخولين فقط لبياناتك.'
      },
      {
        icon: Eye,
        title: 'المراقبة والاكتشاف',
        content: 'أنظمتنا تخضع للمراقبة على مدار الساعة مع اكتشاف واستجابة تلقائية للتهديدات. نحتفظ بسجلات تدقيق شاملة ونجري اختبارات اختراق منتظمة من قبل متخصصين معتمدين.'
      },
      {
        icon: CheckCircle,
        title: 'الامتثال والشهادات',
        content: 'نمتثل لـ GDPR و CCPA ولوائح حماية البيانات المحلية. بنيتنا التحتية مستضافة في مراكز بيانات آمنة ومعتمدة مع ضمان وقت تشغيل 99.9%.'
      }
    ],
    commitment: {
      title: 'التزامنا بالأمان',
      points: [
        'تحديثات وإصلاحات أمنية منتظمة',
        'تدريب الموظفين على الأمان وفحص الخلفيات',
        'خطة الاستجابة للحوادث مع إشعار 24 ساعة',
        'إجراءات النسخ الاحتياطي واستعادة الكوارث',
        'عمليات تدقيق وتقييم أمنية من طرف ثالث'
      ]
    },
    contact: {
      title: 'مخاوف أمنية؟',
      content: 'إذا كان لديك أي مخاوف أمنية أو تريد الإبلاغ عن ثغرة أمنية، يرجى الاتصال بفريق الأمان لدينا فوراً على security@karia.tn'
    }
  }
};

const Security = () => {
  const [lang, setLang] = useState<Language>('fr');
  const t = translations[lang];
  const isRTL = lang === 'ar';

  return (
    <div className="min-h-screen bg-white text-slate-900" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar currentLang={lang} onLanguageChange={setLang} />

      {/* Hero Section */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-16 text-center">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-blue-100 rounded-2xl">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">{t.title}</h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">{t.subtitle}</p>
          <p className="text-sm text-slate-500 mt-4">{t.lastUpdated}</p>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* Security Features Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {t.sections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <div key={index} className="bg-white border border-slate-200 rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <IconComponent className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">{section.title}</h3>
                </div>
                <p className="text-slate-700 leading-relaxed">{section.content}</p>
              </div>
            );
          })}
        </div>

        {/* Security Commitment */}
        <div className="bg-slate-50 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">{t.commitment.title}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {t.commitment.points.map((point, index) => (
              <div key={index} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-slate-700">{point}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <h2 className="text-xl font-semibold text-red-900 mb-4">{t.contact.title}</h2>
          <p className="text-red-700 mb-6">{t.contact.content}</p>
          <a
            href="mailto:security@karia.tn"
            className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-full hover:bg-red-700 transition-colors"
          >
            Contacter l'équipe sécurité
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-8 text-center">
          <div className="flex items-center justify-center gap-2 text-slate-600 mb-4">
            <span>© 2026 karia.tn. Tous droits réservés.</span>
          </div>
          <div className="flex items-center justify-center gap-6 text-sm">
            <a href="/" className="text-slate-600 hover:text-slate-900 transition-colors">{t.backToHome}</a>
            <a href="/privacy" className="text-slate-600 hover:text-slate-900 transition-colors">Confidentialité</a>
            <a href="/terms" className="text-slate-600 hover:text-slate-900 transition-colors">Conditions</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Security;